import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from 'react'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * ShapeManager's commit and live-geometry callbacks, wrapped so the shape being node-edited or
 * cropped follows every transform of it — or the mode exits if the shape becomes locked/frozen
 * (or, for crop, loses its photo) — before the parent hears about the commit.
 */
export function useEditModeCommitSync({
  internalShapeManagerRef,
  nodeEditShapeIdRef,
  setNodeEditShapeData,
  exitNodeEditMode,
  cropShapeIdRef,
  setCropShapeData,
  exitCropMode,
  isNodeEditShapeGeometryBlocked,
  onShapeCommitted,
  onBatchShapesCommitted,
  onLiveSelectionGeometrySync,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  nodeEditShapeIdRef: MutableRefObject<string | null>
  setNodeEditShapeData: Dispatch<SetStateAction<ShapeData | null>>
  exitNodeEditMode: (exitingShapeId: string | undefined) => void
  cropShapeIdRef: MutableRefObject<string | null>
  setCropShapeData: Dispatch<SetStateAction<ShapeData | null>>
  exitCropMode: () => void
  isNodeEditShapeGeometryBlocked: (shape: ShapeData) => boolean
  onShapeCommitted: CenterCanvasConfig['onShapeCommitted']
  onBatchShapesCommitted: CenterCanvasConfig['onBatchShapesCommitted']
  onLiveSelectionGeometrySync: CenterCanvasConfig['onLiveSelectionGeometrySync']
}) {
  // Sync nodeEditShapeData from the canvas store after single-shape resize/move/rotate commits.
  const handleShapeCommitted = useCallback(
    (
      shape: ShapeData,
      source?: 'move' | 'resize' | 'rotate' | 'update',
      undo?: ShapeCommittedUndoMeta
    ) => {
      if (shape.id === nodeEditShapeIdRef.current) {
        if (isNodeEditShapeGeometryBlocked(shape)) {
          exitNodeEditMode(shape.id)
        } else {
          setNodeEditShapeData((prev) => (prev ? shape : prev))
        }
      }

      if (shape.id === cropShapeIdRef.current) {
        if (isNodeEditShapeGeometryBlocked(shape) || !shape.photoFill) {
          exitCropMode()
        } else {
          setCropShapeData((prev) => (prev ? shape : prev))
        }
      }

      onShapeCommitted?.(shape, source, undo)
    },
    [
      isNodeEditShapeGeometryBlocked,
      exitNodeEditMode,
      onShapeCommitted,
      exitCropMode,
      nodeEditShapeIdRef,
      setNodeEditShapeData,
      cropShapeIdRef,
      setCropShapeData,
    ]
  )

  // Wraps onBatchShapesCommitted so that batch rotate/move/resize commits from the Object
  // Panel (which bypass onShapeCommitted) still sync nodeEditShapeData.
  const handleBatchShapesCommitted = useCallback(
    (
      items: BatchShapeTransformItem[],
      source: 'rotate' | 'move' | 'align' | 'resize' | 'update',
      meta?: { historyTxnId?: string }
    ) => {
      const editingId = nodeEditShapeIdRef.current

      if (editingId) {
        const editedItem = items.find((item) => item.shapeId === editingId)

        if (editedItem) {
          // item.shape is only present for canvas-drag commits; for panel commits read from manager
          const updatedShape =
            editedItem.shape ??
            internalShapeManagerRef.current?.getShapes().find((s) => s.id === editingId)

          if (updatedShape) {
            if (isNodeEditShapeGeometryBlocked(updatedShape)) {
              exitNodeEditMode(editingId ?? undefined)
            } else {
              setNodeEditShapeData((prev) => (prev ? updatedShape : prev))
            }
          }
        }
      }

      onBatchShapesCommitted?.(items, source, meta)
    },
    [
      isNodeEditShapeGeometryBlocked,
      exitNodeEditMode,
      onBatchShapesCommitted,
      nodeEditShapeIdRef,
      internalShapeManagerRef,
      setNodeEditShapeData,
    ]
  )

  // Called by ShapeManager after each resize/rotate RAF tick (single-shape and multi-shape).
  // Keeps nodeEditShapeData in sync during live single-shape resize so NodeEditOverlay
  // handles follow the shape while the user is dragging a resize handle.
  const handleLiveSelectionGeometrySync = useCallback(() => {
    const editingId = nodeEditShapeIdRef.current

    if (editingId) {
      const liveShape = internalShapeManagerRef.current?.getShapes().find((s) => s.id === editingId)

      if (liveShape) {
        setNodeEditShapeData((prev) => {
          if (!prev) return prev
          if (
            prev.path === liveShape.path &&
            prev.x === liveShape.x &&
            prev.y === liveShape.y &&
            prev.width === liveShape.width &&
            prev.height === liveShape.height &&
            prev.rotation === liveShape.rotation
          ) {
            return prev
          }

          return liveShape
        })
      }
    }

    onLiveSelectionGeometrySync?.()
  }, [
    onLiveSelectionGeometrySync,
    nodeEditShapeIdRef,
    internalShapeManagerRef,
    setNodeEditShapeData,
  ])

  return { handleShapeCommitted, handleBatchShapesCommitted, handleLiveSelectionGeometrySync }
}
